<template>
  <va-form
    :id="domain ? domain.id : ''"
    class="create-domain__form"
    ref="form"
    :item="domain"
    v-model="form"
    :before-save="beforeSave"
    :after-save="afterSave"
    :modify-before-send="modifyBeforeSend"
    :required-fields-set="requiredFieldsSet">
    <!-- Delete label START -->
    <Modal
      width="400"
      :value="displayDeleteLabelModal"
      :title="$t('resources.labels.label_delete_modal_title')"
      :subtitle="$t('resources.labels.label_delete_modal_subtitle')"
      :cta-text="$t('generic.buttons.delete')"
      :cta-color="'error'"
      :dialog-icon="'danger'"
      @confirm="confirmDeleteLabel"
      @cancel="cancelDeleteLabel">
    </Modal>
    <!-- Delete label END -->
    <!-- Domain connection -->

    <Modal
      width="400"
      :value="isCreateLabelModalOpen"
      hideCloseIcon
      hideActions
      contentClass="label-create-modal"
      @cancel="isCreateLabelModalOpen = false">
      <template #title>
        <v-icon size="24" color="icon">mdi-label-multiple-outline</v-icon>
        <span class="create-label-title">
          {{ $t("resources.domains.domain_label") }}
        </span>
      </template>
      <template>
        <v-form ref="labelForm" @submit.prevent="saveLabel" class="label-form">
          <v-text-field
            class="label-name-input"
            :label="$t('resources.pages.label_name')"
            :placeholder="$t('resources.pages.label_name_placeholder')"
            persistent-placeholder
            v-model="newLabel.name"
            :rules="[
              v => !!v || $t('resources.pages.label_name_required'),
              v => (v && v.length < 11) || $t('resources.pages.label_name_too_long'),
            ]" />
          <va-color-picker :title="$t('resources.pages.label_color')" v-model="newLabel.color" />
          <div class="label-create-button-wrapper">
            <v-btn @click="isCreateLabelModalOpen = false" class="label-cancel-button" outlined>
              {{ $t("resources.pages.cancel") }}
            </v-btn>
            <v-btn type="submit" color="primary">{{ $t("resources.pages.create") }}</v-btn>
          </div>
        </v-form>
      </template>
    </Modal>
    <v-card
      v-if="
        !$can('view', 'original domain connect') &&
        domain.status === DOMAIN_STATUS.CONNECTING &&
        domain.type === DOMAIN_TYPE.CUSTOM
      "
      class="spacing-bottom--24 connection-card">
      <v-card-title class="connection-title">
        {{ $t("resources.domains.connection_title") }}
      </v-card-title>
      <v-card-subtitle class="connection-description spacing-bottom--16">{{
        $t("resources.domains.connection_subtitle")
      }}</v-card-subtitle>
      <div v-for="item in domain.dnsRecords" :key="item.value" class="input-wrapper spacing-bottom--16">
        <v-text-field hide-details disabled class="labeled" :label="item.type" :value="item.name">
          <template #prepend-inner>
            <div class="prepend-wrapper">
              {{
                item.type === "TXT"
                  ? $t("resources.domains.connect_domain_step_2_name")
                  : $t("resources.domains.connect_domain_step_2_host")
              }}
            </div>
          </template>
          <template #append>
            <div class="append-wrapper"><CopyText :value="item.name" /></div>
          </template>
        </v-text-field>
        <v-text-field hide-details disabled :value="item.value">
          <template #prepend-inner>
            <div class="prepend-wrapper">
              {{
                item.type === "TXT"
                  ? $t("resources.domains.connect_domain_step_2_value")
                  : $t("resources.domains.connect_domain_step_2_target")
              }}
            </div>
          </template>
          <template #append>
            <div class="append-wrapper"><CopyText :value="item.value" /></div>
          </template>
        </v-text-field>
      </div>
      <v-btn outlined color="primary" @click="recheckDomainStatus" :loading="recheckLoading">{{
        $t("resources.domains.connection_recheck")
      }}</v-btn>
    </v-card>

    <!-- Details -->
    <v-card class="domain-details__card spacing-bottom--24">
      <div class="d-flex spacing-bottom--16">
        <v-card-title class="mb-0">{{ $t("resources.domains.domain_details") }} </v-card-title>
        <v-card-actions v-if="editForm" class="ml-auto">
          <v-btn
            class="reupload-pages__btn"
            :loading="reuploadPagesLoading"
            @click="reuploadPages"
            color="primary"
            outlined>
            <v-icon small>mdi-refresh</v-icon>
            {{ $t("resources.domains.reupload_pages") }}
          </v-btn>
        </v-card-actions>
      </div>

      <va-text-input
        v-model.trim="form.url"
        @input="domainUrlHandler"
        source="url"
        class="domain-details__card--url"
        required
        :prefix="'https://'"
        :suffix="form.urlChangeAllowed ? MAIN_DOMAIN_URL : ''"
        :label="$t('resources.domains.url')"
        :disabled="!!editForm && !form.urlChangeAllowed"
        :placeholder="'Enteryourdomain.com'"></va-text-input>
      <p v-if="form.urlChangeAllowed" class="domain-details__card--disclaimer">
        {{ $t("resources.domains.change_subdomain_warning") }}
      </p>
      <va-text-input
        class="spacing-top--16"
        source="name"
        required
        :label="$t('resources.domains.domain_name')"
        :placeholder="$t('resources.domains.domain_name_placeholder')"></va-text-input>
      <p class="domain-details__card--disclaimer">{{ $t("resources.domains.details_disclaimer") }}</p>

      <va-text-input
        v-if="ability.can('view', 'google analytics tracking')"
        class="spacing-top--16"
        source="gaPropertyId"
        :label="$t('resources.domains.google_tracking_id')"
        :placeholder="$t('resources.domains.google_tracking_id_placeholder')" />

      <v-autocomplete
        class="label-primary-select spacing-top--16"
        :label="$t('resources.domains.primary_label')"
        :placeholder="$t('resources.pages.label_primary_placeholder')"
        persistent-placeholder
        item-text="name"
        item-value="@id"
        append-icon="mdi-chevron-down"
        :items="labels"
        clearable
        v-model="form.label">
        <template #item="{ item }">
          <v-row class="label-select-item" @mouseover="hoveredLabel = item" @mouseleave="hoveredLabel = null">
            <v-avatar class="label-badge ml-4 mr-2" size="24" :color="item.color"></v-avatar>
            <span class="label-title">{{ item.name }}</span>
            <v-icon
              v-show="hoveredLabel === item"
              class="label-select-item__close-icon"
              :key="item.name"
              @click.prevent.stop="deleteLabel(item)"
              size="16"
              color="icon">
              mdi-trash-can-outline
            </v-icon>
          </v-row>
        </template>
        <template #selection="{ item }">
          <v-avatar class="label-badge mr-2" size="24" :color="item.color"></v-avatar> {{ item.name }}
        </template>
        <template #no-data>
          <div class="v-list-item theme--light">
            <div class="v-list-item__content">
              <div class="v-list-item__title">
                {{ $t("resources.pages.no_labels_found") }}
              </div>
            </div>
          </div>
        </template>
        <template v-slot:append-item>
          <v-list-item class="create-new-label-list-item" ripple @click="isCreateLabelModalOpen = true">
            <v-list-item-content>
              <v-list-item-title class="create-new-label-item">
                <v-icon size="16" color="primary">mdi-plus</v-icon>
                <span>
                  {{ $t("resources.domains.create_new_label") }}
                </span>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
      </v-autocomplete>

      <v-autocomplete
        :class="[
          'additional-label-autocomplete',
          additionalLabelSearch && additionalLabelSearch.length && 'v-autocomplete--search-added',
        ]"
        ref="additionalLabelAutoComplete"
        :label="$t('resources.domains.additional_label')"
        :placeholder="$t('resources.pages.label_additional_placeholder')"
        return-object
        item-text="name"
        :items="labels"
        :search-input.sync="additionalLabelSearch"
        append-icon="mdi-chevron-down"
        prepend-inner-icon="mdi-plus"
        multiple
        persistent-placeholder
        hide-details
        v-model="form.additionalLabels">
        <template #no-data>
          <div class="v-list-item theme--light">
            <div class="v-list-item__content">
              <div class="v-list-item__title">
                {{ $t("resources.pages.no_labels_found") }}
              </div>
            </div>
          </div>
        </template>
        <template #selection> </template>
        <template #item="{ item }">
          <div
            @mouseover="hoveredAdditionalLabel = item"
            @mouseleave="hoveredAdditionalLabel = null"
            class="additional-label-item-wrapper">
            <div class="additional-label-item-inner-wrapper">
              <div class="v-list-item__action">
                <v-icon>{{
                  $refs["additionalLabelAutoComplete"].hasItem(item)
                    ? "mdi-checkbox-marked"
                    : "mdi-checkbox-blank-outline"
                }}</v-icon>
              </div>
              <div class="v-list-item__content">
                <div class="v-list-item__title">
                  <v-avatar class="label-badge mr-2" size="24" :color="item.color"></v-avatar>
                  {{ item.name }}
                </div>
              </div>
            </div>
            <v-icon
              v-show="hoveredAdditionalLabel === item"
              class="label-select-item__close-icon"
              :key="item.name"
              @click.prevent.stop="deleteLabel(item)"
              size="16"
              color="icon">
              mdi-trash-can-outline
            </v-icon>
          </div>
        </template>
        <template v-slot:append-item>
          <v-list-item class="create-new-label-list-item" ripple @click="isCreateLabelModalOpen = true">
            <v-list-item-content>
              <v-list-item-title class="create-new-label-item">
                <v-icon size="16" color="primary">mdi-plus</v-icon>
                <span>
                  {{ $t("resources.domains.create_new_label") }}
                </span>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
      </v-autocomplete>
      <div
        :class="[
          'd-flex',
          'flex-wrap',
          'selection-chip__wrapper',
          form.additionalLabels.length ? 'selection-chip__wrapper--has-selection' : '',
        ]">
        <template v-for="item in form.additionalLabels">
          <v-chip
            class="additional-label-chip"
            :key="`${item.id}-key`"
            :color="getLabelChipColor(item)"
            :style="{ color: item.color }">
            <span class="pr-2">
              {{ item.name }}
            </span>
            <v-icon small @click="$refs['additionalLabelAutoComplete'].selectItem(item)">mdi-close</v-icon>
          </v-chip>
        </template>
      </div>
      <div class="input-wrapper spacing-top--16" v-if="$ability.can('view', 'enable jitsu btn')">
        <v-switch
          class="ml-1 jitsu-switch"
          v-model="form.jitsuEnabled"
          inset
          color="primary"
          :hide-details="'auto'"
          :ripple="false"
          :label="$t('resources.domains.enable_jitsu')"></v-switch>
      </div>
    </v-card>
    <!-- Email service -->
    <v-card class="spacing-bottom--24" v-if="ability.can('view', 'email service')">
      <v-card-title class="spacing-bottom--16"> {{ $t("resources.domains.email_service") }} </v-card-title>
      <va-text-input source="dripId" :label="$t('resources.domains.drip_id')" :placeholder="'123457'"></va-text-input>
    </v-card>
    <!-- Design -->
    <v-card class="spacing-bottom--24">
      <v-card-title> {{ $t("resources.domains.design") }} </v-card-title>
      <v-card-subtitle>{{ $t("resources.domains.design_subtitle") }}</v-card-subtitle>
      <VaImageDragAndDropInput
        :item-id="domain ? domain.favicon : ''"
        class="spacing-bottom--16"
        :label="$t('resources.domains.favicon')"
        @update:photo="updateFavicon"></VaImageDragAndDropInput>
      <VaImageDragAndDropInput
        :item-id="domain ? domain.defaultLogo : ''"
        class="spacing-bottom--16"
        :label="$t('resources.domains.default_logo')"
        @update:photo="updateDefaultLogo"></VaImageDragAndDropInput>
      <VaExpandableInput
        v-if="ability.can('view', 'enable homepage')"
        class="spacing-bottom--16"
        v-model="form.generateHomepage"
        :title="$t('resources.domains.enable_homepage')"
        :sub-title="$t('resources.domains.enable_homepage_subtitle')">
        <p class="domain-subcard-title spacing-bottom--16">{{ $t("resources.domains.choose_theme") }}</p>
        <VaRadioImageCard
          v-model="form.homepageTemplate"
          class="spacing-bottom--16"
          :items="homepageTemplates"></VaRadioImageCard>
        <p class="domain-subcard-title spacing-bottom--16">{{ $t("resources.domains.primary_color") }}</p>
        <VaColorPicker
          class="mb-6"
          :title="$t('resources.domains.primary_colour')"
          v-model="form.primaryColor"></VaColorPicker>
        <p class="domain-subcard-title spacing-bottom--16">{{ $t("resources.domains.homepage_text") }}</p>
        <v-checkbox
          class="spacing-bottom--16"
          hide-details="auto"
          color="primary"
          v-model="showHeadlineFields"
          :label="$t('resources.domains.show_headline')"
          @change="changeShowHeadlineFields"></v-checkbox>
        <div class="spacing-bottom--16" v-if="showHeadlineFields">
          <va-text-input
            v-model="form.homepageHeadline"
            class="spacing-bottom--16"
            :label="$t('resources.domains.headline')"
            :placeholder="$t('resources.domains.add_headline')"></va-text-input>
          <va-text-input
            v-model="form.homepageSubHeadline"
            :label="$t('resources.domains.sub_headline')"
            :placeholder="$t('resources.domains.add_sub_headline')"></va-text-input>
        </div>
        <p class="domain-subcard-title spacing-bottom--16">{{ $t("resources.domains.homepage_categories") }}</p>
        <v-checkbox
          hide-details="auto"
          color="primary"
          v-model="form.generateCategoryPages"
          :label="$t('resources.domains.homepage_categories_label')"></v-checkbox>
      </VaExpandableInput>
      <VaExpandableInput
        v-if="ability.can('view', 'homepage categories')"
        v-model="homepageCategoryBlocksEnabled"
        :title="$t('resources.domains.show_homepage_categories')"
        :sub-title="$t('resources.domains.show_homepage_categories_subtitle')"
        @change="homepageCategoryBlocksEnabledChange">
        <va-array-input
          class="featured-categories-input"
          v-slot="props"
          v-model="form.featureCategories"
          source="featureCategories"
          :label="$t('resources.domains.featured_categories')"
          :add-button-text="$t('resources.domains.homepage_categories_add_text')">
          <v-select
            v-model="form.featureCategories[props.index].type"
            source="type"
            persistent-placeholder
            :placeholder="$t('resources.domains.featured_categories_type_placeholder')"
            :hide-details="'auto'"
            :label="$t('resources.domains.featured_categories_item_type')"
            :items="FEATURED_CATEGORIES_ITEM_TYPES"
            :menu-props="{
              offsetOverflow: true,
              offsetY: true,
            }"
            @change="changeFeaturedCategoryType(props.index)">
            <template v-slot:append>
              <v-icon>mdi-chevron-down</v-icon>
            </template>
          </v-select>
          <va-text-input
            v-if="form.featureCategories[props.index].type === 'link'"
            v-model="form.featureCategories[props.index].text"
            class="spacing-horizontal--16"
            :label="$t('resources.domains.featured_categories_title_label')"
            :placeholder="$t('resources.domains.featured_categories_title_placeholder')"
            required
            v-bind="props"
            source="text"></va-text-input>
          <va-select-input
            v-if="form.featureCategories[props.index].type !== 'link'"
            class="spacing-horizontal--16"
            required
            v-model="form.featureCategories[props.index].category"
            :filled="true"
            :label="$t('resources.pages.page_category')"
            v-bind="props"
            hide-details
            source="category"
            reference="page_categories"
            :filter="resourceFilter"
            :unique-choice="true"
            item-value="@id">
          </va-select-input>

          <va-text-input
            v-if="form.featureCategories[props.index].type === 'link'"
            v-model="form.featureCategories[props.index].link"
            class="spacing-right--16"
            :label="$t('resources.domains.featured_categories_external_link')"
            :placeholder="$t('resources.domains.featured_categories_link_placeholder')"
            required
            :rules="[v => !!urlValidation(v) || $t('generic.validation.url')]"
            v-bind="props"
            source="link"></va-text-input>

          <v-select
            v-model="form.featureCategories[props.index].action"
            persistent-placeholder
            :menu-props="{ offsetY: true, offsetOverflow: true }"
            :hide-details="'auto'"
            :label="$t('generic.open_in')"
            :items="MENU_TAB_TYPES">
            <template v-slot:append>
              <v-icon>mdi-chevron-down</v-icon>
            </template>
          </v-select>
          <va-number-input
            style="display: none"
            disabled
            :value="(form.featureCategories[props.index].position = props.index + 1)"
            required
            v-bind="props"
            source="position"></va-number-input>
        </va-array-input>

        <va-array-input
          v-slot="props"
          v-model="form.displayCategories"
          source="displayCategories"
          :label="$t('resources.domains.display_categories')"
          :add-button-text="$t('resources.domains.homepage_categories_add_text')">
          <va-select-input
            v-model="form.displayCategories[props.index].category"
            :filled="true"
            :label="$t('resources.pages.page_category')"
            v-bind="props"
            hide-details
            source="category"
            reference="page_categories"
            :unique-choice="true"
            :filter="resourceFilter"
            item-value="@id">
          </va-select-input>
          <va-number-input
            style="display: none"
            disabled
            :value="(form.displayCategories[props.index].position = props.index + 1)"
            required
            v-bind="props"
            source="position"></va-number-input>
        </va-array-input>

        <va-array-input
          v-slot="props"
          v-model="form.basicsCategories"
          source="basicsCategories"
          :label="$t('resources.domains.basics_categories')"
          :add-button-text="$t('resources.domains.homepage_categories_add_text')">
          <va-select-input
            v-model="form.basicsCategories[props.index].category"
            :filled="true"
            :label="$t('resources.pages.page_category')"
            v-bind="props"
            hide-details
            source="category"
            :unique-choice="true"
            reference="page_categories"
            :filter="resourceFilter"
            item-value="@id">
          </va-select-input>
          <va-number-input
            style="display: none"
            disabled
            :value="(form.basicsCategories[props.index].position = props.index + 1)"
            required
            v-bind="props"
            source="position"></va-number-input>
        </va-array-input>
      </VaExpandableInput>
    </v-card>

    <!-- SEO -->
    <v-card class="spacing-bottom--24">
      <v-card-title class="spacing-bottom--16"> {{ $t("resources.domains.seo") }} </v-card-title>
      <va-toggle-block
        v-model="allowIndexing"
        class="spacing-bottom--16"
        :title="$t('resources.domains.allow_indexing')"
        :sub-title="$t('resources.domains.allow_indexing_subtitle')"
        @change="changeAllowIndexing">
        <div>
          <va-text-input
            :rows="2"
            v-model="form.robots"
            :label="$t('resources.domains.robots')"
            multiline></va-text-input>
          <p class="domain-details__card--disclaimer">{{ $t("resources.domains.custom_indexing") }}</p>
        </div>
      </va-toggle-block>

      <VaExpandableInput
        v-model="showCustomCodeFields"
        :title="$t('resources.domains.custom_tracking_code')"
        :sub-title="$t('resources.domains.custom_tracking_code_subtitle')"
        @change="changeShowCustomCodeFields">
        <code-input v-model="form.headCustomCode" :title="$t('resources.domains.head_section_tracking_code')">
        </code-input>
        <p class="domain-details__card--disclaimer spacing-bottom--16">
          {{ $t("resources.domains.head_section_tracking_code_disclaimer") }}
        </p>
        <code-input v-model="form.bodyCustomCode" :title="$t('resources.domains.body_section_tracking_code')">
        </code-input>
        <p class="domain-details__card--disclaimer spacing-bottom--16">
          {{ $t("resources.domains.body_section_tracking_code_disclaimer") }}
        </p>
      </VaExpandableInput>
    </v-card>

    <!-- Adsense -->
    <v-card class="spacing-bottom--16" v-if="ability.can('view', 'adsense')">
      <v-card-title class="spacing-bottom--16"> {{ $t("resources.domains.adsense") }} </v-card-title>
      <code-input
        v-model="form.adsenseOne"
        class="spacing-bottom--16"
        :title="$t('resources.domains.adsense_above_sidebar')">
      </code-input>
      <code-input v-model="form.adsenseTwo" :title="$t('resources.domains.adsense_below_sidebar')"> </code-input>
    </v-card>

    <v-card v-if="displayDeleteCard" class="spacing-bottom--16">
      <div class="d-flex">
        <div class="d-flex flex-column">
          <v-card-title>{{ $t("resources.domains.delete_domain_title") }}</v-card-title>
          <v-card-subtitle class="mb-0">{{ $t("resources.domains.delete_domain_subtitle") }}</v-card-subtitle>
        </div>
        <v-card-actions class="ml-auto">
          <v-btn :loading="deleteDomainLoading" @click="deleteDomain" color="body" outlined>
            {{ $t("resources.domains.delete_domain_cta") }}
          </v-btn>
        </v-card-actions>
      </div>
    </v-card>

    <required-field-text></required-field-text>
  </va-form>
</template>

<script>
  import definedAbility from "@/ability";
  // Input components
  import VaImageDragAndDropInput from "@/components/custom/VaImageDragAndDropInput";
  import VaToggleBlock from "@/components/custom/VaToggleBlock";
  import VaExpandableInput from "@/components/custom/VaExpandableInput";
  import VaRadioImageCard from "@/components/custom/VaRadioImageCard";
  import VaColorPicker from "@/components/custom/VaColorPicker";
  import CodeInput from "@/components/custom/CodeInput";
  import RequiredFieldText from "@/components/custom/RequiredFieldText";
  import Modal from "@/components/custom/Modal";
  // Mixins
  import crudFormMixin from "@/mixins/crudFormMixin";
  // Constants
  import {
    ROBOTS,
    BLOGS_TEMPLATES,
    FEATURED_CATEGORIES_ITEM_TYPES,
    MENU_TAB_TYPES,
    DOMAIN_TYPE,
    DOMAIN_STATUS,
    MAIN_DOMAIN_URL,
  } from "@/constant";
  // Utils
  import { reuploadPages } from "@/utility/reuploadPages";
  import { urlValidation } from "@/utility/urlValidation";
  import CopyText from "@/components/custom/CopyText";
  import { formErrorMessage } from "@/utility/formErrorMessage";

  export default {
    name: "DomainForm",
    components: {
      VaImageDragAndDropInput,
      VaToggleBlock,
      VaExpandableInput,
      VaRadioImageCard,
      VaColorPicker,
      CodeInput,
      RequiredFieldText,
      CopyText,
      Modal,
    },
    mixins: [crudFormMixin],
    props: {
      domain: {
        type: Object,
        default: () => {},
      },
      editForm: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        urlValidation,
        FEATURED_CATEGORIES_ITEM_TYPES,
        DOMAIN_TYPE,
        MENU_TAB_TYPES,
        DOMAIN_STATUS,
        MAIN_DOMAIN_URL,
        ability: definedAbility(),
        requiredFormFields: ["name", "url"],
        form: {
          homepageTemplate: "default",
          jitsuEnabled: true,
        },
        resourceFilter: {},
        homepageTemplates: BLOGS_TEMPLATES,
        showHeadlineFields: false,
        showCustomCodeFields: false,
        allowIndexing: false,
        deleteDomainLoading: false,
        reuploadPagesLoading: false,
        homepageCategoryBlocksEnabled: false,
        recheckLoading: false,
        labels: [],
        labelListLoading: false,
        hoveredLabel: null,
        displayDeleteLabelModal: false,
        labelToDelete: {},
        additionalLabelSearch: "",
        hoveredAdditionalLabel: null,
        isCreateLabelModalOpen: false,
        newLabel: {},
      };
    },
    beforeMount() {
      // Set the resource filter for select inputs (Filter by domain)
      this.resourceFilter = this.domain?.id ? { domain: this.domain?.id } : {};

      // If we have homepageHeadline or homepageSubHeadline set, display those inputs (same for custom code fields)
      this.showHeadlineFields = !!(this.domain?.homepageHeadline || this.domain?.homepageSubHeadline);
      this.showCustomCodeFields = !!(this.domain?.headCustomCode || this.domain?.bodyCustomCode);
      // Transform some of the form values. (Comes back as object from back-end, but has to be transformed to IRI)
      this.form = this.domain ?? this.form;
      this.form.favicon = this.domain?.favicon?.["@id"];
      this.form.defaultLogo = this.domain?.defaultLogo?.["@id"];
      /** By default, disallow indexing in robots.txt (if no value is set) */
      this.form.robots = this.domain?.robots ? this.domain.robots : ROBOTS.disallow;
      this.form.jitsuEnabled = this.domain?.jitsuEnabled ?? this.form.jitsuEnabled;
      this.allowIndexing = this.form.robots !== ROBOTS.disallow;
      this.homepageCategoryBlocksEnabled =
        (this.form.basicsCategories && !!this.form.basicsCategories.length) ||
        (this.form.displayCategories && !!this.form.displayCategories.length) ||
        (this.form.featureCategories && !!this.form.featureCategories.length);

      this.form.featureCategories = this.domain?.featureCategories?.map(featureCategory => {
        if (featureCategory.type === "page_category") {
          return {
            ...featureCategory,
            category: featureCategory.category["@id"],
          };
        } else {
          return featureCategory;
        }
      });
      this.form.displayCategories = this.domain?.displayCategories?.map(featureCategory => {
        return {
          ...featureCategory,
          category: featureCategory.category["@id"],
        };
      });
      this.form.basicsCategories = this.domain?.basicsCategories?.map(featureCategory => {
        return {
          ...featureCategory,
          category: featureCategory.category["@id"],
        };
      });

      this.form.adsenseOne = this.domain?.adsenseOne || "";
      this.form.adsenseTwo = this.domain?.adsenseTwo || "";

      this.form.headCustomCode = this.domain?.headCustomCode || "";
      this.form.bodyCustomCode = this.domain?.bodyCustomCode || "";
      this.form.urlChangeAllowed = this.domain?.urlChangeAllowed || false;

      if (this.form.urlChangeAllowed) {
        this.form.url = this.form.url.replace(MAIN_DOMAIN_URL, "");
      }
      this.form.additionalLabels = this.domain?.additionalLabels || [];

      this.loadLabels();
    },
    computed: {
      displayDeleteCard() {
        if (this.editForm && this.domain.default && this.ability.can("delete", "default domain")) {
          return true;
        } else if (this.editForm && !this.domain.default) {
          return true;
        } else {
          return false;
        }
      },
    },
    methods: {
      async saveLabel() {
        if (!this.$refs.labelForm.validate()) {
          return;
        }
        try {
          const response = await this.$admin.dataProvider.create("labels", {
            data: {
              name: this.newLabel.name,
              color: this.newLabel.color ?? "#FFFFFF",
            },
          });
          this.newLabel = {};
          this.$admin.toast.success(this.$t("resources.pages.label_create_success"));
          this.labels.push(response.data);
          this.labels.sort((a, b) => a.name.localeCompare(b.name));
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        } finally {
          this.isCreateLabelModalOpen = false;
        }
      },
      deleteLabel(item) {
        this.displayDeleteLabelModal = true;
        this.labelToDelete = item;
      },
      getLabelChipColor(item) {
        let hex = item.color;
        let r = parseInt(hex.slice(1, 3), 16);
        let g = parseInt(hex.slice(3, 5), 16);
        let b = parseInt(hex.slice(5, 7), 16);

        return "rgba(" + r + ", " + g + ", " + b + ", " + 0.12 + ")";
      },
      async confirmDeleteLabel() {
        try {
          await this.$admin.dataProvider.customHttpCall("DELETE", `labels/${this.labelToDelete.id}`);
          this.$admin.toast.success(this.$t("resources.pages.label_deleted_successfully"));
        } catch (error) {
          this.$admin.toast.error(this.$t("resources.pages.label_delete_error"));
        } finally {
          this.labels = this.labels.filter(item => item["@id"] !== this.labelToDelete["@id"]);
          if (
            typeof this.form.label === "object" &&
            this.form.label &&
            this.form.label["@id"] === this.labelToDelete["@id"]
          ) {
            this.form.label = null;
          } else if (this.form.label && this.form.label === this.labelToDelete["@id"]) {
            this.form.label = null;
          }
          this.form.additionalLabels = this.form.additionalLabels.filter(
            item => item["@id"] !== this.labelToDelete["@id"],
          );
        }
        this.displayDeleteLabelModal = false;
        this.labelToDelete = {};
      },
      cancelDeleteLabel() {
        this.labelToDelete = {};
        this.displayDeleteLabelModal = false;
      },
      async loadLabels() {
        this.labelListLoading = true;
        try {
          const response = await this.$admin.dataProvider.getList("labels", {
            disablePagination: true,
            sort: [{ by: "name", desc: false }],
          });
          this.labels = response.data;
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        } finally {
          this.labelListLoading = false;
        }
      },
      modifyBeforeSend(data) {
        if (this.form.urlChangeAllowed) {
          data.url = `${data.url}${MAIN_DOMAIN_URL}`;
        }

        if (data.additionalLabels) {
          data.additionalLabels = data.additionalLabels.map(label => label["@id"]);
        }

        if (typeof data.label === "object" && data.label !== null) {
          data.label = data.label["@id"];
        }

        return data;
      },
      changeFeaturedCategoryType(index) {
        if (this.form.featureCategories[index].type === "link") {
          delete this.form.featureCategories[index].page;
          delete this.form.featureCategories[index].category;
          this.form.featureCategories[index].link = "";
        } else if (this.form.featureCategories[index].type === "page_category") {
          delete this.form.featureCategories[index].page;
          delete this.form.featureCategories[index].link;
          delete this.form.featureCategories[index].text;
          this.form.featureCategories[index].category = "";
        }
      },
      domainUrlHandler(val) {
        // Regex to avoid spaces in URLs and makes the text lowercase
        this.form.url = val.toLowerCase().replace(/ /g, "");
      },
      async deleteDomain() {
        if (
          await this.$admin.confirm(
            this.$t("resources.domains.delete_domain_confirmation_title"),
            this.$t("resources.domains.delete_domain_confirmation_subtitle"),
            this.$t("overrides.confirm.delete_message"),
          )
        ) {
          try {
            this.deleteDomainLoading = true;
            await this.$admin.dataProvider.delete("domains", {
              id: this.domain.id,
            });
            this.$admin.toast.success(this.$t("resources.domains.delete_domain_successful"));
            this.$router.push({ name: "domains_list" });
          } catch (e) {
            this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
          } finally {
            this.deleteDomainLoading = false;
          }
        }
      },
      changeAllowIndexing(val) {
        if (!val) {
          this.form.robots = ROBOTS.disallow;
        }
        if (val) {
          this.form.robots = ROBOTS.allow;
        }
      },
      homepageCategoryBlocksEnabledChange(val) {
        if (!val) {
          this.form.featureCategories = [];
          this.form.displayCategories = [];
          this.form.basicsCategories = [];
        }
      },
      changeShowHeadlineFields(val) {
        if (!val) {
          this.form.homepageHeadline = "";
          this.form.homepageSubHeadline = "";
        }
      },
      changeShowCustomCodeFields(val) {
        if (!val) {
          this.form.headCustomCode = "";
          this.form.bodyCustomCode = "";
        }
      },
      updateDefaultLogo(newValue) {
        if (newValue === "") {
          this.form.defaultLogo = null;
        } else {
          this.form.defaultLogo = newValue["@id"];
        }
      },
      updateFavicon(newValue) {
        if (newValue === "") {
          this.form.favicon = null;
        } else {
          this.form.favicon = newValue["@id"];
        }
      },
      submit() {
        if (this.form.generateHomepage === false) {
          this.form.featureCategories.forEach(element => {
            element.category = element.category?.["@id"] ? element.category["@id"] : element.category;
          });
          this.form.displayCategories.forEach(element => {
            element.category = element.category?.["@id"] ? element.category["@id"] : element.category;
          });
          this.form.basicsCategories.forEach(element => {
            element.category = element.category?.["@id"] ? element.category["@id"] : element.category;
          });
        }

        this.$route.meta.disableRedirect = false;
        this.$refs.form.onSubmit();
      },
      async reuploadPages() {
        this.reuploadPagesLoading = true;

        try {
          const response = await reuploadPages(this.domain.id);
          this.$admin.toast.success(response.message);
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        } finally {
          this.reuploadPagesLoading = false;
        }
      },
      async recheckDomainStatus() {
        try {
          this.recheckLoading = true;
          const { data } = await this.$admin.dataProvider.customHttpCall("GET", `domains/${this.domain.id}/dns/check`);
          if (data.message.valid) {
            this.$admin.toast.success(this.$t("resources.domains.connection_recheck_success"));
          } else {
            this.$admin.toast.error(this.$t("resources.domains.connection_recheck_error"));
          }
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        } finally {
          this.recheckLoading = false;
        }
      },
      copyValue() {
        setTimeout(() => {
          return this.$t("resources.domains.connect_domain_step_2_copied");
        }, 3000);

        return this.$t("resources.domains.connect_domain_step_2_copied");
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "../../@core/preset/preset/functions.scss";
  @import "../../@core/preset/preset/typography/index.scss";

  .featured-categories-input {
    &::v-deep {
      .item {
        .va-array-input__item-wrapper {
          & > div {
            &:first-of-type {
              flex: unset;
              flex-basis: 33%;
            }
          }
        }
      }
    }
  }
  .domain-details__card {
    &--disclaimer {
      @include text-caption();
      color: get-color("subtitle");
      margin-top: 8px;
    }
    &--url {
      &::v-deep {
        &.v-text-field__prefix {
          background-color: get-color("element-bg-200") !important;
        }

        .v-text-field__suffix {
          @include text-body-2();
          background-color: get-color("element-bg-200") !important;
          padding: 10px 16px;
          border-left: 1px solid get-color("border");
          color: get-color("body") !important;
          border-bottom-right-radius: 4px;
          border-top-right-radius: 4px;
        }

        &.v-input--is-disabled .v-input__slot {
          background-color: get-color("hover") !important;
        }
      }
    }
  }
  .domain-subcard-title {
    @include text-body-1-bold();
    color: get-color("subtitle");
  }

  .reupload-pages__btn {
    padding: 12px 24px 12px 16px !important;
    &::v-deep {
      i {
        margin-right: 6px;
      }
    }
  }

  .connection-card {
    .input-wrapper {
      display: flex;
      gap: 16px;

      &::v-deep {
        .v-text-field {
          pointer-events: unset;
          flex: 1;
        }
      }
    }

    &::v-deep {
      .v-input__slot {
        align-items: stretch;
        background-color: get-color("white") !important;
      }

      .v-input__append-inner {
        padding: 0 !important;
        margin: 0;
      }

      .v-input__prepend-inner {
        align-self: stretch !important;
        padding: 0;
        margin: 0;
      }

      .labeled {
        .v-input__prepend-inner {
          width: 0;
        }
        .v-text-field__slot {
          input {
            margin-left: 45px;
          }
        }
      }
    }

    .prepend-wrapper {
      background: get-color("element-bg-200");
      display: flex;
      align-items: center;
      padding: 8px;
      border-radius: 3px 0 0 3px;
      color: get-color("body");
    }

    .append-wrapper {
      @include text-button-small();
      display: flex;
      align-items: center;
      padding: 12px;
      border-radius: 0 3px 3px 0;
      color: get-color("primary");
      cursor: pointer;
    }

    .connection-title {
      @include heading-3();
      color: get-color("headline");
    }

    .connection-description {
      @include text-body-2();
      color: get-color("body");
    }
  }

  .label-primary-select {
    ::v-deep {
      .v-input__append-inner {
        padding: 0;

        svg {
          width: 20px;
          height: 20px;
        }
      }
    }
  }

  .label-select-item {
    padding: 8px 0;
    &:hover {
      .label-select-item__close-icon {
        display: inline-flex !important;
      }
    }
    &__close-icon {
      margin-left: auto;
      margin-right: 16px;
    }
  }

  .label-title {
    @include text-body-2();
  }

  .label-badge {
    border: 1px solid get-color("border") !important;
  }

  .additional-label-autocomplete {
    ::v-deep {
      .v-select__selections input::placeholder {
        color: get-color("subtitle") !important;
        opacity: 1 !important;
      }

      .v-list-item {
        @include text-body-2();
      }
    }
  }

  .additional-label-autocomplete:not(.v-autocomplete--search-added) {
    ::v-deep {
      .v-select__selections::after {
        position: absolute;
        content: attr(data-placeholder);
        color: get-color("subtitle") !important;
        opacity: 1 !important;
      }
    }
  }

  .additional-label-item-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  .additional-label-item-inner-wrapper {
    display: flex;
    align-items: center;
  }

  .additional-label-chip {
    @include text-caption();
  }

  .create-new-label-item {
    display: flex;
    justify-content: center;
    gap: 4px;
    ::v-deep span {
      @include text-button-medium();
      color: get-color("primary");
    }
  }

  .create-new-label-list-item {
    border-top: 1px solid get-color("border");
  }

  .create-label-title {
    @include heading-3();
    color: get-color("headline");
    margin-left: 6px;
  }

  .label-create-button-wrapper {
    display: flex;
    gap: 16px;
    margin-top: 16px;

    button {
      flex: 1;
    }
  }

  .label-form {
    margin-top: 16px;

    ::v-deep {
      .v-messages__message {
        margin-top: 4px !important;
        margin-bottom: 8px;
        transition-duration: 0.2s;
      }
    }
  }

  .label-create-modal {
    overflow-y: unset;
  }

  .jitsu-switch {
    &::v-deep {
      .v-input--selection-controls__input {
        margin-right: 8px;
      }
      label {
        @include text-caption-bold();
        color: get-color("subtitle");
      }
    }
  }
</style>
